import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { API } from '../App';

/**
 * Iter 210 — Backup email verification landing page.
 *
 * Hit when the user clicks the magic link inside the verification email
 * (`/verify-backup-email?token=<magicToken>`). We POST the token to the
 * backend to flip the device row's `recoveryEmailVerified` flag. After a
 * brief success message we forward them to /preferences.
 *
 * Distinct from the existing business-signup `VerifyEmail.jsx` flow,
 * which targets a different backend endpoint and use case.
 */
const VerifyBackupEmail = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const token = search.get('token') || '';
  // Compute initial state from URL directly so we don't have to call setState
  // synchronously inside the effect (avoids react-hooks/set-state-in-effect).
  const [status, setStatus] = useState(token ? 'loading' : 'error');
  const [errorDetail, setErrorDetail] = useState(token ? '' : 'Missing verification token');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) return;
    let alive = true;
    let redirectTimer;
    (async () => {
      try {
        const r = await axios.post(`${API}/me/email/verify`, { token });
        if (!alive) return;
        setEmail(r.data?.email || '');
        setStatus('ok');
        redirectTimer = setTimeout(() => navigate('/preferences'), 1800);
      } catch (err) {
        if (!alive) return;
        setErrorDetail(err?.response?.data?.detail || 'Verification failed');
        setStatus('error');
      }
    })();
    return () => {
      alive = false;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="verify-backup-email-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-sm w-full text-center space-y-3">
        {status === 'loading' && (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-violet-600 mx-auto" />
            <p className="text-sm text-slate-600">Verifying your email…</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <div className="inline-flex p-3 bg-emerald-50 rounded-full">
              <ShieldCheck className="h-7 w-7 text-emerald-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Email verified</h2>
            <p className="text-[13px] text-slate-500">
              {email ? <><strong className="break-all">{email}</strong> is now your backup email.</> : 'Your backup email is now active.'}
            </p>
            <p className="text-[12px] text-slate-400">Redirecting to your preferences…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="inline-flex p-3 bg-red-50 rounded-full">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Couldn&apos;t verify</h2>
            <p className="text-[13px] text-red-700">{errorDetail}</p>
            <Link to="/preferences" className="inline-block mt-2 text-sm text-violet-700 hover:underline" data-testid="verify-backup-back-btn">
              Back to notification settings
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyBackupEmail;
